import React, { useEffect, useState } from "react";
import { Coins, TrendingUp, TrendingDown, Clock } from "lucide-react";
import api, { money } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const TYPE_LABEL = {
  TRADE_IN: "Trade-In", REDEMPTION: "Redemption", PURCHASE_REWARD: "Purchase Reward",
  REFUND_REVERSAL: "Refund Reversal", PROMOTION: "Promotion", ADMIN_ADJUSTMENT: "Adjustment", EXPIRY: "Expiry",
};

export default function MyRicePoints() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [tradeins, setTradeins] = useState([]);

  useEffect(() => {
    api.get("/ricepoints/summary").then(({ data }) => setSummary(data));
    api.get("/ricepoints/ledger").then(({ data }) => setLedger(data));
    api.get("/tradein").then(({ data }) => setTradeins(data));
  }, []);

  const redemptions = ledger.filter((l) => l.type === "REDEMPTION");

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><Coins className="text-primary" /> My RicePoints</h1>
      <p className="text-muted-foreground mt-1">Earn RicePoints on trade-ins and redeem up to 15% of your eligible order value.</p>

      {/* Balance hero */}
      <div className="mt-6 bg-secondary text-secondary-foreground rounded-2xl p-6 sm:p-8 relative overflow-hidden grain-texture">
        <div className="text-sm text-secondary-foreground/70">Current RicePoints Balance</div>
        <div className="font-heading font-extrabold text-5xl sm:text-6xl font-mono-points mt-1" data-testid="rp-balance">
          {summary ? money(summary.balance) : "…"}
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat icon={Coins} label="Available" value={summary?.balance} />
          <Stat icon={TrendingUp} label="Total Earned" value={summary?.total_earned} />
          <Stat icon={TrendingDown} label="Total Redeemed" value={summary?.total_redeemed} />
          <Stat icon={Clock} label="Pending" value={summary?.pending} />
        </div>
      </div>

      <Tabs defaultValue="ledger" className="mt-8">
        <TabsList data-testid="rp-tabs">
          <TabsTrigger value="ledger" data-testid="tab-ledger">Full Ledger</TabsTrigger>
          <TabsTrigger value="tradein" data-testid="tab-tradein">Trade-In History</TabsTrigger>
          <TabsTrigger value="redeem" data-testid="tab-redeem">Redemptions</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger">
          <LedgerTable rows={ledger.map((l) => ({
            date: l.datetime, cols: [TYPE_LABEL[l.type] || l.type, l.reference_id || "—"],
            points: l.points, balance: l.new_balance,
          }))} headers={["Type", "Reference"]} />
        </TabsContent>

        <TabsContent value="tradein">
          <div className="mt-4 overflow-x-auto bg-card border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left"><tr>
                <th className="p-3">Date</th><th className="p-3">Rice Received</th><th className="p-3">Points Earned</th><th className="p-3">Status</th>
              </tr></thead>
              <tbody>
                {tradeins.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No trade-ins yet.</td></tr>}
                {tradeins.map((t) => (
                  <tr key={t.trade_id} className="border-t border-border" data-testid={`ti-row-${t.trade_id}`}>
                    <td className="p-3 whitespace-nowrap">{t.created_at?.slice(0, 10)}</td>
                    <td className="p-3">{t.actual_weight ? `${t.actual_weight} kg` : `~${t.estimated_kg} kg (est.)`}</td>
                    <td className="p-3 font-mono-points">{t.points_credited ? money(t.points_credited) : `~${money(t.estimated_points)}`}</td>
                    <td className="p-3"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="redeem">
          <LedgerTable rows={redemptions.map((l) => ({
            date: l.datetime, cols: [l.reference_id || "—"], points: l.points, balance: l.new_balance,
          }))} headers={["Order"]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/10 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs text-secondary-foreground/70"><Icon size={13} /> {label}</div>
      <div className="font-mono-points font-bold text-lg mt-0.5">{value == null ? "…" : money(value)}</div>
    </div>
  );
}

function LedgerTable({ rows, headers }) {
  return (
    <div className="mt-4 overflow-x-auto bg-card border border-border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left"><tr>
          <th className="p-3">Date</th>{headers.map((h) => <th key={h} className="p-3">{h}</th>)}
          <th className="p-3 text-right">Points</th><th className="p-3 text-right">Balance</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={headers.length + 3} className="p-6 text-center text-muted-foreground">No entries.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              <td className="p-3 whitespace-nowrap">{r.date?.slice(0, 10)}</td>
              {r.cols.map((c, j) => <td key={j} className="p-3 font-mono-points text-xs">{c}</td>)}
              <td className={`p-3 text-right font-mono-points font-semibold ${r.points >= 0 ? "text-[hsl(var(--secondary))]" : "text-primary"}`}>{r.points >= 0 ? "+" : ""}{money(r.points)}</td>
              <td className="p-3 text-right font-mono-points">{money(r.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    "Points Credited": "bg-[hsl(var(--secondary))] text-white",
    Accepted: "bg-[hsl(var(--secondary))]/80 text-white",
    Rejected: "bg-destructive text-white",
    Cancelled: "bg-muted-foreground/30",
  };
  return <span className={`text-xs font-semibold px-2 py-1 rounded-md ${map[status] || "bg-primary/15 text-primary"}`}>{status}</span>;
}
